import { __rest } from 'tslib';
import { parse, isValid, startOfWeek, endOfWeek, isSameMonth, isSameYear } from 'date-fns';
import dateFnsFormat from 'date-fns/format';
import 'date-fns/endOfWeek';
import 'date-fns/isSameDay';
import 'date-fns/isSameWeek';
import 'date-fns/isLastDayOfMonth';
import 'date-fns/isFirstDayOfMonth';
import 'date-fns/isWithinInterval';
import 'date-fns/startOfWeek';
import { getWeekStartOn } from './getWeekDetails.js';

const RANGE_FORMATS = {
    sameMonthWithYear: { formatTo: ['d', 'd MMM yyyy'], sizes: { small: '168px', medium: '180px', large: '186px' } },
    differentMonthWithYear: {
        formatTo: ['d MMM', 'd MMM yyyy'],
        sizes: { small: '168px', medium: '180px', large: '186px' },
    },
    differentYear: { formatTo: ['d MMM yyyy', 'd MMM yyyy'], sizes: { small: '168px', medium: '180px', large: '186px' } },
    sameMonth: { formatTo: ['d', 'd MMM'], sizes: { small: '144px ', medium: '148px', large: '140px' } },
    differentMonth: { formatTo: ['d MMM', 'd MMM'], sizes: { small: '144px ', medium: '148px', large: '140px' } },
};
const SINGLE_FORMATS_SIZES = {
    'EEEE, d MMM yyyy': { small: '180px', medium: '180px', large: '186px' },
    'EEEE, d MMM': { small: '140px', medium: '152px', large: '160px' },
    'd MMM yyyy': { small: '100px', medium: '112px', large: '120px' },
    'd MMM': { small: '80px', medium: '84px', large: '94px' },
    P: { small: '100px', medium: '110px', large: '110px' },
    PP: { small: '100px', medium: '112px', large: '120px' },
    PPP: { small: '140px', medium: '152px', large: '160px' },
    PPPP: { small: '220px', medium: '230px', large: '230px' },
};
const getRangeFormatData = ([start, end], formatWithYear, allowSingleRange) => {
    if (start && isValid(start) && end && isValid(end)) {
        if (isSameMonth(start, end)) {
            return RANGE_FORMATS[formatWithYear ? 'sameMonthWithYear' : 'sameMonth'];
        }
        else if (isSameYear(start, end)) {
            return RANGE_FORMATS[formatWithYear ? 'differentMonthWithYear' : 'differentMonth'];
        }
    }
    else if (!isValid(start) || !isValid(end)) {
        if (formatWithYear) {
            return RANGE_FORMATS[allowSingleRange ? 'sameMonthWithYear' : 'differentYear'];
        }
        return RANGE_FORMATS[allowSingleRange ? 'sameMonth' : 'differentMonth'];
    }
    return RANGE_FORMATS.differentYear;
};
const getSingleFormatData = (formatTo) => {
    let sizes = SINGLE_FORMATS_SIZES[formatTo];
    if (!sizes) {
        sizes = { small: '180px', medium: '180px', large: '180px' };
    }
    return { formatTo, sizes };
};
const formatRange = ([start = null, end = null], labelFormat, { hasNoEnd, noEndInputLabel, allowSingleRange, isWeekPicker, weekStartsOn, locale }) => {
    const [label, endLabel = '...'] = [start, end].map((date, i) => {
        let dateToFormat = date;
        if (!date && allowSingleRange) {
            const weekStart = getWeekStartOn({ date: start, weekStartsOn });
            dateToFormat = isWeekPicker && start ? endOfWeek(start, { weekStartsOn: weekStart }) : start;
        }
        if (dateToFormat && isValid(dateToFormat)) {
            return dateFnsFormat(dateToFormat, labelFormat[i], { locale });
        }
    });
    let finalEndLabel = endLabel;
    if (hasNoEnd) {
        finalEndLabel = noEndInputLabel;
    }
    if (label) {
        return `${label} – ${finalEndLabel}`;
    }
};
const getLabelData = (value, dateFormat, _a) => {
    var { labelFormat = 'EEEE, d MMM yyyy', // this should be PPPP to work with locales
    formatWithYear, isWeekPicker, weekStartsOn = 0, isRangePicker, customRange, allowSingleRange, locale } = _a, rest = __rest(_a, ["labelFormat", "formatWithYear", "isWeekPicker", "weekStartsOn", "isRangePicker", "customRange", "allowSingleRange", "locale"]);
    let range = customRange !== null && customRange !== void 0 ? customRange : [null, null];
    if (isRangePicker && value) {
        const [start = null, end = null] = value.split(' - ').map(raw => {
            if (raw) {
                return parse(raw, dateFormat, new Date(), { locale });
            }
            return null;
        });
        range = [start, end];
    }
    let date = value ? parse(value, dateFormat, new Date(), { locale }) : null;
    if (!isValid(date)) {
        date = null;
    }
    if (isWeekPicker && !isRangePicker && date) {
        const weekStart = getWeekStartOn({ date, weekStartsOn });
        range = [startOfWeek, endOfWeek].map(util => util(date, { weekStartsOn: weekStart }));
    }
    if (isRangePicker || isWeekPicker || customRange) {
        const { formatTo, sizes } = getRangeFormatData(range, formatWithYear, allowSingleRange);
        const label = formatRange(range, formatTo, Object.assign(Object.assign({}, rest), { allowSingleRange, isWeekPicker, weekStartsOn, locale }));
        if (label) {
            return { label, sizes };
        }
        return { sizes };
    }
    const { formatTo, sizes } = getSingleFormatData(labelFormat);
    const label = date ? dateFnsFormat(date, formatTo, { locale }) : undefined;
    if (label) {
        return { label, sizes };
    }
    return { sizes };
};

export { getLabelData };
//# sourceMappingURL=getLabelData.js.map
